<template>
  <div class="contents">
    <div class="layout">
      <div class="breadcrumb">
        <div class="breadcrumb-item">시스템 관리</div>
        <div class="breadcrumb-item active">역할 권한 관리</div>
      </div>

      <div class="page-header">
        <h1 class="page-title">역할 권한 관리</h1>
        <div class="page-actions">
          <button 
            v-permission="'ADMIN_ROLE_WRITE'" 
            @click="openCreateModal" 
            class="btn btn-primary"
          >
            <i class="icon-plus"></i>
            역할 추가
          </button>
        </div>
      </div>

      <div class="white-panel mt20">
        <div class="table-wrap">
          <div class="scroll-box">
            <table class="table-type1">
              <colgroup>
                <col style="width: 5%;">  <!-- NO -->
                <col style="width: 20%;"> <!-- 역할명 -->
                <col style="width: 30%;"> <!-- 설명 -->
                <col style="width: 15%;"> <!-- 사용자 수 -->
                <col style="width: 15%;"> <!-- 생성일 -->
                <col style="width: 15%;"> <!-- 액션 -->
              </colgroup>
              <thead>
                <tr>
                  <th>NO</th>
                  <th>역할명</th>
                  <th>설명</th>
                  <th>사용자 수</th>
                  <th>생성일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(role, index) in roles" :key="role.id">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <strong>{{ role.name }}</strong>
                  </td>
                  <td>{{ role.description || '-' }}</td>
                  <td>
                    <span class="user-count">{{ role.userCount }}명</span>
                  </td>
                  <td>{{ formatDate(role.createdAt) }}</td>
                  <td>
                    <div class="action-buttons">
                      <button 
                        v-permission="'ADMIN_ROLE_READ'" 
                        @click="openPermissionModal(role)" 
                        class="btn-sm btn-info"
                      >
                        권한보기
                      </button>
                      <button 
                        v-permission="'ADMIN_ROLE_WRITE'" 
                        @click="openEditModal(role)" 
                        class="btn-sm btn-outline"
                      >
                        수정
                      </button>
                      <button 
                        v-if="role.userCount === 0" 
                        v-permission="'ADMIN_ROLE_DELETE'" 
                        @click="confirmDelete(role)" 
                        class="btn-sm btn-danger"
                      >
                        삭제
                      </button>
                      <span v-else class="text-muted text-sm">삭제불가</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="roles.length === 0">
                  <td colspan="6" class="text-center text-muted">
                    등록된 역할이 없습니다.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 역할 생성/수정 모달 -->
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-content large" @click.stop>
          <div class="modal-header">
            <h3>{{ modalMode === 'create' ? '역할 추가' : '역할 수정' }}</h3>
            <button @click="closeModal" class="modal-close">&times;</button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitForm">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">역할명 *</label>
                  <input 
                    id="name"
                    v-model="form.name" 
                    type="text" 
                    class="form-control" 
                    required
                  >
                </div>
                <div class="form-group">
                  <label for="description">설명</label>
                  <input 
                    id="description"
                    v-model="form.description" 
                    type="text" 
                    class="form-control"
                  >
                </div>
              </div>

              <div class="form-group">
                <label>권한 설정</label>
                <div class="permissions-grid">
                  <div 
                    v-for="menuGroup in groupedPermissions" 
                    :key="menuGroup.menuCode" 
                    class="permission-group"
                  >
                    <div class="permission-group-header">
                      <h4>{{ menuGroup.menuCode }}</h4>
                      <div class="permission-group-actions">
                        <button 
                          type="button"
                          @click="toggleMenuPermissions(menuGroup.menuCode, true)"
                          class="btn-xs btn-outline"
                        >
                          전체 선택
                        </button>
                        <button 
                          type="button"
                          @click="toggleMenuPermissions(menuGroup.menuCode, false)"
                          class="btn-xs btn-outline"
                        >
                          전체 해제
                        </button>
                      </div>
                    </div>
                    <div class="permission-items">
                      <label 
                        v-for="permission in menuGroup.permissions" 
                        :key="permission.id" 
                        class="permission-item"
                      >
                        <input 
                          type="checkbox" 
                          :value="permission.id"
                          v-model="form.selectedPermissions"
                        >
                        <span class="permission-label">
                          {{ permission.actionType }}
                          <small class="permission-desc">{{ permission.description }}</small>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button @click="closeModal" class="btn btn-secondary">취소</button>
            <button @click="submitForm" class="btn btn-primary">
              {{ modalMode === 'create' ? '추가' : '수정' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 권한 보기 모달 -->
      <div v-if="showPermissionModal" class="modal-overlay" @click="closePermissionModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ selectedRole?.name }} 권한 정보</h3>
            <button @click="closePermissionModal" class="modal-close">&times;</button>
          </div>
          <div class="modal-body">
            <div v-if="selectedRole?.permissions?.length > 0">
              <div class="permission-list">
                <div 
                  v-for="permission in selectedRole.permissions" 
                  :key="permission.id"
                  class="permission-badge"
                >
                  {{ permission.menuCode }}_{{ permission.actionType }}
                  <small>{{ permission.description }}</small>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted">
              할당된 권한이 없습니다.
            </div>
          </div>
          <div class="modal-footer">
            <button @click="closePermissionModal" class="btn btn-secondary">닫기</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from 'vue'
import { apiClient } from '@/services/auth-header'
import { useToast } from '@/composables/useToast'

const toast = useToast()

// 데이터
const roles = ref([])
const availablePermissions = ref([])
const loading = ref(false)

// 모달 관련
const showModal = ref(false)
const showPermissionModal = ref(false)
const modalMode = ref('create') // 'create' or 'edit'
const selectedRole = ref(null)

const form = reactive({
  id: null,
  name: '',
  description: '',
  selectedPermissions: []
})

// 권한을 메뉴별로 그룹화
const groupedPermissions = computed(() => {
  const groups = {}
  
  availablePermissions.value.forEach(permission => {
    if (!groups[permission.menuCode]) {
      groups[permission.menuCode] = {
        menuCode: permission.menuCode,
        permissions: []
      }
    }
    groups[permission.menuCode].permissions.push(permission)
  })
  
  return Object.values(groups)
})

// 페이지 로드 시 데이터 조회
onMounted(async () => {
  await Promise.all([
    fetchRoles(),
    fetchAvailablePermissions()
  ])
})

// 역할 목록 조회
const fetchRoles = async () => {
  try {
    loading.value = true
    const response = await apiClient.get('/admin/roles')
    roles.value = response.data
  } catch (error) {
    toast.error('역할 목록을 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

// 사용 가능한 권한 목록 조회
const fetchAvailablePermissions = async () => {
  try {
    const response = await apiClient.get('/admin/roles/available-permissions')
    availablePermissions.value = response.data
  } catch (error) {
    console.error('권한 목록 조회 실패:', error)
  }
}

// 생성 모달 열기
const openCreateModal = () => {
  modalMode.value = 'create'
  resetForm()
  showModal.value = true
}

// 수정 모달 열기
const openEditModal = (role) => {
  modalMode.value = 'edit'
  form.id = role.id
  form.name = role.name
  form.description = role.description || ''
  form.selectedPermissions = role.permissions ? role.permissions.map(p => p.id) : []
  showModal.value = true
}

// 권한 보기 모달 열기
const openPermissionModal = (role) => {
  selectedRole.value = role
  showPermissionModal.value = true
}

// 모달 닫기
const closeModal = () => {
  showModal.value = false
  resetForm()
}

const closePermissionModal = () => {
  showPermissionModal.value = false
  selectedRole.value = null
}

// 폼 초기화
const resetForm = () => {
  form.id = null
  form.name = ''
  form.description = ''
  form.selectedPermissions = []
}

// 메뉴별 권한 전체 선택/해제
const toggleMenuPermissions = (menuCode, select) => {
  const menuPermissions = availablePermissions.value
    .filter(p => p.menuCode === menuCode)
    .map(p => p.id)
  
  if (select) {
    // 선택: 기존 선택된 권한에 메뉴 권한 추가
    const newPermissions = [...form.selectedPermissions]
    menuPermissions.forEach(id => {
      if (!newPermissions.includes(id)) {
        newPermissions.push(id)
      }
    })
    form.selectedPermissions = newPermissions
  } else {
    // 해제: 메뉴 권한 제거
    form.selectedPermissions = form.selectedPermissions.filter(id => 
      !menuPermissions.includes(id)
    )
  }
}

// 폼 제출
const submitForm = async () => {
  try {
    const roleData = {
      name: form.name,
      description: form.description,
      permissionIds: form.selectedPermissions
    }

    if (modalMode.value === 'create') {
      await apiClient.post('/admin/roles', roleData)
      toast.success('역할이 성공적으로 추가되었습니다.')
    } else {
      await apiClient.put(`/admin/roles/${form.id}`, roleData)
      toast.success('역할이 성공적으로 수정되었습니다.')
    }
    
    closeModal()
    await fetchRoles()
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        (modalMode.value === 'create' ? '역할 추가에 실패했습니다.' : '역할 수정에 실패했습니다.')
    toast.error(errorMessage)
  }
}

// 삭제 확인
const confirmDelete = (role) => {
  if (confirm(`'${role.name}' 역할을 삭제하시겠습니까?`)) {
    deleteRole(role.id)
  }
}

// 역할 삭제
const deleteRole = async (id) => {
  try {
    await apiClient.delete(`/admin/roles/${id}`)
    toast.success('역할이 성공적으로 삭제되었습니다.')
    await fetchRoles()
  } catch (error) {
    const errorMessage = error.response?.data?.message || '역할 삭제에 실패했습니다.'
    toast.error(errorMessage)
  }
}

// 유틸리티 함수
const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('ko-KR')
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 5px;
  align-items: center;
}

.user-count {
  font-weight: 500;
  color: #007bff;
}

.modal-content.large {
  max-width: 800px;
  max-height: 90vh;
}

.form-row {
  display: flex;
  gap: 20px;
}

.form-row .form-group {
  flex: 1;
}

.permissions-grid {
  border: 1px solid #ddd;
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.permission-group {
  border-bottom: 1px solid #eee;
}

.permission-group:last-child {
  border-bottom: none;
}

.permission-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.permission-group-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.permission-group-actions {
  display: flex;
  gap: 5px;
}

.permission-items {
  padding: 12px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.permission-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.permission-item:hover {
  background-color: #f8f9fa;
}

.permission-label {
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.permission-desc {
  font-size: 12px;
  color: #666;
}

.permission-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.permission-badge {
  display: inline-flex;
  flex-direction: column;
  padding: 8px 12px;
  background-color: #e9ecef;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.permission-badge small {
  font-weight: normal;
  color: #666;
  margin-top: 2px;
}

.btn-info {
  background-color: #17a2b8;
  color: white;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 11px;
}

/* 기존 스타일과 동일한 공통 스타일들 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #dee2e6;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-outline {
  background-color: transparent;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #6c757d;
}

.text-sm {
  font-size: 12px;
}
</style>